/* eslint-disable import/no-duplicates */
import flatten from 'lodash/flatten'
import isArray from 'lodash/isArray'
import mergeWith from 'lodash/mergeWith'
import uniq from 'lodash/uniq'

import { SortDirection } from './enums'
import { SearchSuggestion, SortModel } from './types'

import { PermissionComponents } from '@/api/permissions-client'
import { PricingTierComponents } from '@/api/pricing-tier-client'

export const splitSortIntoFieldAndDirection = (sort: string): SortModel => {
  if (!sort) {
    return {
      field: 'filename',
      direction: SortDirection.ASCENDING
    }
  }
  const [field, direction] = sort.split(':')

  return {
    field,
    direction: (direction as SortDirection) || SortDirection.ASCENDING
  }
}

export const getNewSortDirection = (
  isSameField: boolean,
  currentSortDirection: SortDirection
): SortDirection => {
  return isSameField && currentSortDirection === SortDirection.ASCENDING
    ? SortDirection.DESCENDING
    : SortDirection.ASCENDING
}

export const yn = (input: string): boolean => {
  const value = input.trim().toLowerCase()

  if (/^(?:n|no|nein|false|0)$/i.test(value)) {
    return false
  }

  return Boolean(value)
}

function customizer(objValue: any, srcValue: any) {
  if (isArray(objValue)) {
    return uniq(flatten(objValue.concat(srcValue)))
  }
}

export const parseFieldValuesSuggestionMap = <T>(
  data: T[],
  enableFilteringFields: string[]
) => {
  return data?.reduce((acc: any, it: any) => {
    const tmp: any = {}

    enableFilteringFields.forEach((field: any) => {
      if (it[field] != null) {
        switch (field) {
          case '360_roles':
            if (it[field]?.length > 0) {
              const roleNames = it[field]
                .filter(Boolean)
                .map((elem: any) => elem.name)

              tmp[field] = Array.isArray(tmp[field])
                ? tmp[field].push(...roleNames)
                : [...roleNames]
            }
            break
          case 'organization':
            if (it[field]) {
              tmp[field] = Array.isArray(tmp[field])
                ? tmp[field].push(it[field].name)
                : [it[field].name]
            }
            break
          default:
            tmp[field] = Array.isArray(tmp[field])
              ? tmp[field].push(it[field])
              : [it[field]]
        }
      }
    })

    return mergeWith(acc, tmp, customizer)
  }, {})
}

export const parseSearchAndFilterSuggestion = (
  searchAndFilterValues: SearchSuggestion[]
) => {
  const filterKeyValueMap: Record<string, string> = {}
  let searchValue: string | undefined = undefined

  searchAndFilterValues.forEach((searchAndFilterValue) => {
    if (searchAndFilterValue.type === 'filter') {
      const [filterKey, filterValue] = (searchAndFilterValue.value ?? '').split(
        ':'
      )

      if (filterKey && filterValue) {
        filterKeyValueMap[filterKey] = filterValue
      }
    }

    if (searchAndFilterValue.type === 'search') {
      searchValue = searchAndFilterValue.value
    }
  })

  return { filterKeyValueMap, searchValue }
}

export const isValidJSON = (json: string) => {
  try {
    JSON.parse(json)

    return true
  } catch (e) {
    return false
  }
}

export const unsensitiveFilter = (row: any, id: any, value: string[]) => {
  try {
    const rowValue = row.getValue(id)!.toString()?.toLowerCase()

    return value.some((v) => rowValue.includes(v?.toLowerCase()))
  } catch (error) {
    return false
  }
}

/** Orgs who do have a Pricing Tier: Business / Professional / etc. */
export const getGrantsForPricingTier = (
  pricingTier: PricingTierComponents.Schemas.PricingTier
) => {
  const grants: PermissionComponents.Schemas.Grant[] = []

  // all organizations have access to entities
  grants.push({
    action: 'entity:*'
  })

  // access to schema via entity builder
  grants.push({
    action: 'schema:*'
  })

  // user management
  grants.push({
    action: 'user:*'
  })

  // role management
  grants.push({
    action: 'role:*'
  })

  // organization management
  grants.push({
    action: 'organization:*'
  })

  // access to messaging
  grants.push({
    action: 'message:*'
  })

  // workflow builder
  grants.push({
    action: 'workflow:definition:*'
  })

  // workflow execution
  grants.push({
    action: 'workflow:execution:*'
  })

  // 360 dashboard
  grants.push({
    action: 'dashboard:*'
  })

  // access to design builder
  grants.push({
    action: 'design:*'
  })

  // access to automation
  grants.push({
    action: 'automation:*'
  })

  // access to tokens
  grants.push({
    action: 'token:*'
  })

  // notes
  grants.push({
    action: 'note:*'
  })

  // email settings
  grants.push({
    action: 'email_setting:*'
  })

  // epilot email address
  grants.push({
    action: 'epilot_email_address:*'
  })

  // meter readings
  grants.push({
    action: 'meter_reading:*'
  })

  grants.push(...convertSettingsToGrants(pricingTier))

  return grants
}

const convertSettingsToGrants = (
  pricingTier: PricingTierComponents.Schemas.PricingTier
) => {
  if (!pricingTier) {
    return []
  }

  const grants: PermissionComponents.Schemas.Grant[] = []

  const settings = {
    ...(pricingTier.settings || {}),
    ...(pricingTier.override_settings || {})
  }

  Object.entries(settings).forEach(
    ([key, value]: [key: string, value: any]) => {
      // only process boolean value
      if (value?.enabled === true) {
        const action = `${key}:*`

        grants.push({ action })
      }
    }
  )

  return grants
}
